import { useCallback } from 'react';
import { createUsePuck, useGetPuck } from '@puckeditor/core';
import { useThemeConfiguration } from '../../context/ThemeConfigurationContext';
import { THEMED_BLOCK_KINDS, themedBlockProps } from '../themedBlocks';

const usePuckApi = createUsePuck();

/**
 * Champs composites : accès aux props du bloc sélectionné + écriture de
 * props AUTRES que celle du champ (le onChange d'un champ Puck ne couvre que
 * sa prop) via dispatch `replace` — pattern ProposalSectionColumnsField.
 */
export function useSelectedItemProps() {
  // `dispatch` et `getSelectorForId` se lisent au moment de l'écriture : Puck
  // les recrée à chaque changement d'état, s'y abonner rendait
  // `updateItemProps` instable et faisait re-rendre tout consommateur qui l'a
  // en dépendance.
  const getPuck = useGetPuck();
  // s.selectedItem est FIGÉ à la sélection : il n'est pas recalculé quand la
  // data change (dispatch replace — édition inline canvas, sync rich text).
  // On relit l'item vivant par id à chaque changement de store, sinon les
  // champs sidebar reçoivent des props périmées (et l'éditeur rich écrase
  // les changements fraîchement appliqués avec l'ancien HTML).
  const selectedItem = usePuckApi((s) => {
    const sel = s.selectedItem;
    const id = sel?.props?.id;
    if (!id) return sel ?? null;
    try {
      // `getItemById` lève un TypeError quand l'id n'est plus dans l'index —
      // le bloc vient d'être supprimé, ou remonté après un rafraîchissement.
      return s.getItemById(id) ?? sel;
    } catch {
      return sel;
    }
  });

  const updateItemProps = useCallback((patch) => {
    const { dispatch, getSelectorForId, resolveDataById } = getPuck();
    const id = selectedItem?.props?.id;
    const selector = id ? getSelectorForId(id) : null;
    if (!selector) return;
    dispatch({
      type: 'replace',
      destinationZone: selector.zone,
      destinationIndex: selector.index,
      data: {
        type: selectedItem.type,
        props: { ...selectedItem.props, ...patch },
      },
    });
    // Un `replace` dispatché à la main ne passe pas par `resolveData`, que
    // Puck n'appelle que pour ses propres champs : sans cette relance, un
    // réglage fait ici ne détacherait pas le bloc de son style du thème
    // (cf. puck/themedBlocks).
    resolveDataById?.(id, 'replace');
  }, [getPuck, selectedItem]);

  // Un bloc qui suit un style du thème s'affiche avec les valeurs ACTUELLES
  // du style (lien vivant, cf. themedBlocks) : ses champs les montrent aussi,
  // pas la copie posée quand on l'a lié.
  const themeConfiguration = useThemeConfiguration();
  const props = selectedItem?.props ?? {};
  const kind = THEMED_BLOCK_KINDS[selectedItem?.type];
  const itemProps = kind && themeConfiguration
    ? themedBlockProps(kind, props, { themeConfiguration })
    : props;

  return { selectedItem, itemProps, updateItemProps };
}
